import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ouvrirFichier } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Search,
  Plus,
  FileText,
  Euro,
  Calendar,
  Building2,
  Loader2,
  MoreVertical,
  Trash2,
  Edit,
  Eye,
  Upload,
  CheckCircle,
  Clock,
  XCircle,
  Receipt,
  Download,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Fournitures bureau",
  "Matériel cuisine",
  "Marketing / Communication",
  "Assurances",
  "Loyer / Charges",
  "Véhicule",
  "Services externes",
  "Événements",
  "Autre"
];

const STATUTS = {
  paye: { label: "Payé", color: "bg-green-100 text-green-800", icon: CheckCircle },
  non_paye: { label: "Non payé", color: "bg-red-100 text-red-800", icon: XCircle },
  en_cours: { label: "En cours", color: "bg-amber-100 text-amber-800", icon: Clock }
};

export default function JustificatifsAchatPage() {
  const [justificatifs, setJustificatifs] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedJustificatif, setSelectedJustificatif] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    description: "",
    montant: "",
    date_achat: "",
    fournisseur: "",
    categorie: "",
    statut: "non_paye"
  });
  const [fichier, setFichier] = useState(null);

  useEffect(() => {
    loadJustificatifs();
  }, []);

  const loadJustificatifs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/justificatifs-achat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJustificatifs(response.data.justificatifs);
      setTotaux(response.data.totaux);
    } catch (error) {
      toast.error("Erreur lors du chargement des justificatifs");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      description: "",
      montant: "",
      date_achat: "",
      fournisseur: "",
      categorie: "",
      statut: "non_paye"
    });
    setFichier(null);
  };

  const handleCreate = async () => {
    if (!formData.description) {
      toast.error("La description est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const data = new FormData();
      data.append("description", formData.description);
      if (formData.montant) data.append("montant", formData.montant);
      if (formData.date_achat) data.append("date_achat", formData.date_achat);
      if (formData.fournisseur) data.append("fournisseur", formData.fournisseur);
      if (formData.categorie) data.append("categorie", formData.categorie);
      data.append("statut", formData.statut);
      if (fichier) data.append("fichier", fichier);

      await axios.post(`${API}/api/justificatifs-achat`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Justificatif ajouté");
      setShowAddDialog(false);
      resetForm();
      loadJustificatifs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.description) {
      toast.error("La description est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/justificatifs-achat/${selectedJustificatif.id}`, {
        description: formData.description,
        montant: formData.montant ? parseFloat(formData.montant) : null,
        date_achat: formData.date_achat || null,
        fournisseur: formData.fournisseur || null,
        categorie: formData.categorie || null,
        statut: formData.statut
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Justificatif mis à jour");
      setShowEditDialog(false);
      resetForm();
      loadJustificatifs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/justificatifs-achat/${selectedJustificatif.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Justificatif supprimé");
      setShowDeleteDialog(false);
      setSelectedJustificatif(null);
      loadJustificatifs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleStatusChange = async (id, newStatut) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/api/justificatifs-achat/${id}/statut?statut=${newStatut}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Statut mis à jour");
      loadJustificatifs();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const openEditDialog = (justificatif) => {
    setSelectedJustificatif(justificatif);
    setFormData({
      description: justificatif.description || "",
      montant: justificatif.montant?.toString() || "",
      date_achat: justificatif.date_achat || "",
      fournisseur: justificatif.fournisseur || "",
      categorie: justificatif.categorie || "",
      statut: justificatif.statut || "non_paye"
    });
    setShowEditDialog(true);
  };

  // Filter justificatifs
  const filteredJustificatifs = justificatifs.filter(j => {
    const matchSearch = !searchTerm || 
      j.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      j.fournisseur?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      j.categorie?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatut = filterStatut === "all" || j.statut === filterStatut;
    
    return matchSearch && matchStatut;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="justificatifs-achat-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary flex items-center gap-3">
            <Receipt className="w-8 h-8" />
            Justificatifs d'Achat
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des factures et justificatifs d'achat
          </p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-justificatif-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un justificatif
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau justificatif d'achat</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="Description de l'achat..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.montant}
                    onChange={(e) => setFormData({...formData, montant: e.target.value})}
                    data-testid="montant-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date d'achat</Label>
                  <Input
                    type="date"
                    value={formData.date_achat}
                    onChange={(e) => setFormData({...formData, date_achat: e.target.value})}
                    data-testid="date-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Fournisseur</Label>
                <Input
                  placeholder="Nom du fournisseur"
                  value={formData.fournisseur}
                  onChange={(e) => setFormData({...formData, fournisseur: e.target.value})}
                  data-testid="fournisseur-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={formData.categorie} 
                    onValueChange={(v) => setFormData({...formData, categorie: v})}
                  >
                    <SelectTrigger data-testid="categorie-select">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select 
                    value={formData.statut} 
                    onValueChange={(v) => setFormData({...formData, statut: v})}
                  >
                    <SelectTrigger data-testid="statut-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non_paye">Non payé</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="paye">Payé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Fichier (PDF, image...)</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => setFichier(e.target.files[0])}
                  data-testid="fichier-input"
                />
                {fichier && (
                  <p className="text-sm text-muted-foreground">{fichier.name}</p>
                )}
              </div>
              
              <Button 
                className="w-full" 
                onClick={handleCreate}
                disabled={saving}
                data-testid="save-justificatif-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Ajouter
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {totaux && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold text-primary">{totaux.total_montant?.toFixed(2)} €</p>
                </div>
                <Euro className="w-8 h-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Payé</p>
                  <p className="text-2xl font-bold text-green-600">{totaux.total_paye?.toFixed(2)} €</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Non payé</p>
                  <p className="text-2xl font-bold text-red-600">{totaux.total_non_paye?.toFixed(2)} €</p>
                </div>
                <XCircle className="w-8 h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">En cours</p>
                  <p className="text-2xl font-bold text-amber-600">{totaux.total_en_cours?.toFixed(2)} €</p>
                </div>
                <Clock className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par description, fournisseur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[180px]" data-testid="filter-statut">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="non_paye">Non payé</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="paye">Payé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-secondary">
            Justificatifs ({filteredJustificatifs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredJustificatifs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun justificatif trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Fournisseur</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Fichier</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJustificatifs.map((j) => {
                    const statutInfo = STATUTS[j.statut] || STATUTS.non_paye;
                    const StatutIcon = statutInfo.icon;
                    
                    return (
                      <TableRow key={j.id}>
                        <TableCell>
                          <p className="font-medium">{j.description}</p>
                        </TableCell>
                        <TableCell>{j.fournisseur || "-"}</TableCell>
                        <TableCell>
                          {j.categorie && (
                            <Badge variant="outline">{j.categorie}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {j.date_achat ? format(parseISO(j.date_achat), "dd/MM/yyyy", { locale: fr }) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {j.montant ? `${j.montant.toFixed(2)} €` : "-"}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Badge className={`${statutInfo.color} cursor-pointer`}>
                                <StatutIcon className="w-3 h-3 mr-1" />
                                {statutInfo.label}
                              </Badge>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleStatusChange(j.id, "non_paye")}>
                                <XCircle className="w-4 h-4 mr-2 text-red-500" />
                                Non payé
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(j.id, "en_cours")}>
                                <Clock className="w-4 h-4 mr-2 text-amber-500" />
                                En cours
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleStatusChange(j.id, "paye")}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                                Payé
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                        <TableCell>
                          {j.fichier_url ? (
                            <a 
                              href={`${API}${j.fichier_url}`} 
                              onClick={(e) => { e.preventDefault(); ouvrirFichier(j.fichier_url).catch(() => toast.error("Document indisponible ou accès refusé")); }}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              <Download className="w-4 h-4" />
                              <span className="text-sm">{j.fichier_nom || "Fichier"}</span>
                            </a>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(j)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedJustificatif(j);
                                  setShowDeleteDialog(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le justificatif</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.montant}
                  onChange={(e) => setFormData({...formData, montant: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Date d'achat</Label>
                <Input
                  type="date"
                  value={formData.date_achat}
                  onChange={(e) => setFormData({...formData, date_achat: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Input
                value={formData.fournisseur}
                onChange={(e) => setFormData({...formData, fournisseur: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select 
                  value={formData.categorie} 
                  onValueChange={(v) => setFormData({...formData, categorie: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select 
                  value={formData.statut} 
                  onValueChange={(v) => setFormData({...formData, statut: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_paye">Non payé</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="paye">Payé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button 
              className="w-full" 
              onClick={handleUpdate}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce justificatif ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le justificatif et son fichier associé seront supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
